import { FormikProps } from 'formik';
import * as yup from 'yup';
import { IsAllow, SCHEMES_RESPONSE } from '../../../../redux-store/types/portfolio';
import { DEFAULT_MAX_INVESTMENT, getDateString } from '../../../../utils/constants';
import { isMobile } from '../../../quick-links/faq';
import { isRegular } from '../AdditionalPurchase/ap-invest';
import { idcwDetailsType } from '../IdcwOptions';
import { InvestThrough } from '../Switch/types';
import { Values } from './STPInvestment';
import {
    InstallmentFieldsType,
    InstallmentKeyType,
    STPAPIRequestType,
    UpperValidationOptions,
} from './types';

export const InvestUpperValidationSchema = ({ planType }: UpperValidationOptions) => {
    return isRegular(planType) ? validateRegularScheme : validateDirectScheme;
};

export const InvestLowerValidationScheme = ({ swpFlag }: { swpFlag: boolean }) => {
    let _installmentKey: InstallmentKeyType = {
        minAmount: 1,
        maxAmount: DEFAULT_MAX_INVESTMENT,
        installment: 1,
    };
    let _multiples = 1;
    let _installmentError = false;

    const isAmountValid = (amount: number) => {
        if (!amount) return false;
        if (
            amount < _installmentKey.minAmount ||
            amount > _installmentKey.maxAmount ||
            amount % _multiples !== 0
        ) {
            return false;
        }
        return true;
    };
    return yup.object({
        amount: yup.string().when('installmentKey', {
            is: (installmentKey: InstallmentKeyType) => (_installmentKey = installmentKey),
            then: yup.string().when('multiples', {
                is: (multiples: number) => (_multiples = multiples),
                then: yup.string().test('checkAmount', (amount) => isAmountValid(Number(amount))),
            }),
        }),
        // stpType: !!swpFlag
        //     ? yup.string().notRequired()
        //     : yup.string().required('Select a STP option'),
        toDate: yup.date().when('installmentError', {
            is: (installmentError: boolean) => (_installmentError = installmentError),
            then: yup.date().test('checkInstallment', () => !_installmentError),
        }),
        frequency: yup.string().required('Select a frequency'),
    });
};

const validateRegularScheme = () =>
    yup.object({
        arn1: yup.string().required('Please enter valid ARN Code'),
        euin: yup.string().required('Select a EUIN option'),
        euinCode: yup.string().when('euin', {
            is: (euin: IsAllow | string) => euin === IsAllow.YES,
            then: yup.string().required('EUIN Code is required'),
        }),
        euinDeclaration: yup.boolean().when('euin', {
            is: (euin: IsAllow | string) => euin === IsAllow.No,
            then: yup.boolean().oneOf([true], 'Please check EUIN declaration'),
        }),
        isChecked: yup.boolean().oneOf([true], 'Please Accept the terms and conditions'),
    });

const validateDirectScheme = () =>
    yup.object({
        riaCode: yup.string().when('investThrough', {
            is: (investThrough: InvestThrough) => investThrough === InvestThrough.ria,
            then: yup.string().required('Please enter valid RIA Code'),
        }),
        isChecked: yup.boolean().oneOf([true], 'Please Accept the terms and conditions'),
    });

export const parseToAPIStructure = (
    installmentFormik: FormikProps<InstallmentFieldsType>,
    stpFromScheme: SCHEMES_RESPONSE,
    stpToScheme?: SCHEMES_RESPONSE,
    idcwDetails?: idcwDetailsType | null,
    formik?: FormikProps<Values>,
): STPAPIRequestType => {
    const { amount, fromDate, toDate, frequency, installmentDay, stpType } =
        installmentFormik.values;
    const {
        investThrough,
        arn1: distId,
        arn2: subDist,
        arn3: subBrokerARN,
        euinCode: euin,
        euinDeclaration,
        riaCode,
        riaFlag,
    } = formik?.values || { arn1: '', arn2: '', arn3: '', euinCode: '', euin: '', riaCode: '', };
    const apiBody: STPAPIRequestType = {
        data: [
            {
                source: isMobile(window.navigator.userAgent) ? 'MOBILE' : 'WEB',
                // otpEmail: email || '',
                // otpMobile: mobile || '',
                // otpSentTo: userRegisteredWith === 'EMAIL' ? 'E': 'M',
                amc: stpFromScheme.fundAmcCode || stpFromScheme.amc,
                schemeOptions: [
                    {
                        folio: String(stpToScheme?.folio || stpFromScheme.folio),
                        trxnType: !!formik ? 'STP' : 'SWP',
                        fromIsin: stpFromScheme.isin,
                        toIsin: stpToScheme?.isin || '',
                        trxnDate: '',
                        trxnTime: '',
                        reInvest: idcwDetails?.idcwOption || '',
                        amount,
                        startDate: getDateString(fromDate),
                        endDate: getDateString(toDate),
                        frequency,
                        variableType: '',
                        stpSwpDay: '' + (fromDate?.getDate() || installmentDay || ''),
                        distId:
                            investThrough === InvestThrough.direct
                                ? 'DIRECT'
                                : investThrough !== InvestThrough.arn
                                    ? ''
                                    : `ARN-${distId}`,
                        subDist,
                        subBrokerARN: subBrokerARN ? `ARN-${subBrokerARN}` : '',
                        euin: euin ? `E${euin}` : '',
                        euinDeclarationFlag: !!euinDeclaration ? IsAllow.YES : IsAllow.No,
                        riaCode: !!riaCode ? `${riaFlag}-${riaCode}` : '',
                    },
                ],
            },
        ],
    };
    return apiBody;
};
